import React, { useRef, useEffect, useState } from 'react';
import './TapBasketballGame.css';

const TapBasketballGame = () => {
  // Reference to the game container and ball element.
  const containerRef = useRef(null);
  const ballElRef = useRef(null);

  // Game state for score and hoop positions.
  const [score, setScore] = useState(0);
  // We'll store the vertical (y) positions for our left/right hoops.
  const [hoops, setHoops] = useState({
    left: { y: 200 },
    right: { y: 300 }
  });

  // Our physics "ball" object is stored in a ref so that updates do not force React re‑renders.
  const ball = useRef({
    x: 100,         // starting x-position
    y: 100,         // starting y-position
    vx: 0,          // horizontal velocity
    vy: 0,          // vertical velocity
    radius: 20      // ball radius in pixels
  });

  // To prevent multiple score events when the ball "lingers" in a hoop,
  // we use flags that are reset once the ball leaves the hoop area.
  const scoredFlag = useRef({ left: false, right: false });

  // We'll use a ref for the container dimensions.
  const containerSize = useRef({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Add state for physics parameters
  const [physicsSettings, setPhysicsSettings] = useState({
    gravity: 0.3,
    forceMagnitude: 15.5,
    restitution: 0.7
  });

  const FLOOR_HEIGHT = 100; // Height of floor from bottom of screen

  // The main animation/physics update loop.
  const update = () => {
    // (For simplicity we treat dt = 1 per frame.)
    const dt = 1;
    const b = ball.current;

    // Use physics settings from state
    b.vy += physicsSettings.gravity * dt;
    b.x += b.vx * dt;
    b.y += b.vy * dt;

    // Get container dimensions.
    const { width, height } = containerSize.current;
    const floorY = height - FLOOR_HEIGHT; // Calculate floor position

    // Bounce off the left/right boundaries.
    if (b.x < b.radius) {
      b.x = b.radius;
      b.vx = -b.vx * physicsSettings.restitution;
    }
    if (b.x > width - b.radius) {
      b.x = width - b.radius;
      b.vx = -b.vx * physicsSettings.restitution;
    }
    // Bounce off the top
    if (b.y < b.radius) {
      b.y = b.radius;
      b.vy = -b.vy * physicsSettings.restitution;
    }
    // Bounce off the floor instead of bottom of screen
    if (b.y > floorY - b.radius) {
      b.y = floorY - b.radius;
      b.vy = -b.vy * physicsSettings.restitution;
    }

    // Update the ball's DOM position using CSS transforms.
    if (ballElRef.current) {
      ballElRef.current.style.transform = `translate(${b.x - b.radius}px, ${b.y - b.radius}px)`;
    }

    // ---- Check for "scoring" ----
    // We assume that each hoop is drawn as a horizontal rim.
    // (You can improve the collision area to match your visuals.)
    const hoopWidth = 60;
    const hoopHeight = 10;
    // Left hoop is fixed at x = 20.
    const leftHoopX = 20;
    // Right hoop is fixed at x = (container width – hoopWidth – 20).
    const rightHoopX = width - hoopWidth - 20;

    // Check the left hoop.
    const leftHoopY = hoops.left.y;
    if (
      b.x > leftHoopX &&
      b.x < leftHoopX + hoopWidth &&
      b.y > leftHoopY &&
      b.y < leftHoopY + hoopHeight
    ) {
      if (!scoredFlag.current.left) {
        scoredFlag.current.left = true;
        setScore((s) => s + 1);
        // When a basket is scored, change the vertical positions of both hoops.
        const newLeftY = Math.random() * (height / 2 - 50) + 50;
        const newRightY = Math.random() * (height / 2 - 50) + 50;
        setHoops({ left: { y: newLeftY }, right: { y: newRightY } });
      }
    } else {
      scoredFlag.current.left = false;
    }

    // Check the right hoop.
    const rightHoopY = hoops.right.y;
    if (
      b.x > rightHoopX &&
      b.x < rightHoopX + hoopWidth &&
      b.y > rightHoopY &&
      b.y < rightHoopY + hoopHeight
    ) {
      if (!scoredFlag.current.right) {
        scoredFlag.current.right = true;
        setScore((s) => s + 1);
        const newLeftY = Math.random() * (height / 2 - 50) + 50;
        const newRightY = Math.random() * (height / 2 - 50) + 50;
        setHoops({ left: { y: newLeftY }, right: { y: newRightY } });
      }
    } else {
      scoredFlag.current.right = false;
    }

    // Schedule the next frame.
    requestAnimationFrame(update);
  };

  // On mount, set up the container size and start the animation loop.
  useEffect(() => {
    const updateContainerSize = () => {
      containerSize.current = { width: window.innerWidth, height: window.innerHeight };
    };
    updateContainerSize();
    window.addEventListener('resize', updateContainerSize);

    requestAnimationFrame(update);

    return () => window.removeEventListener('resize', updateContainerSize);
  }, []);

  // When the user taps (or clicks) the screen, we calculate the vector from
  // the tap to the ball and add an opposite force. (Using onPointerDown makes
  // it work on both desktop and mobile.)
  const handlePointerDown = (e) => {
    // Get tap coordinates relative to the container.
    const rect = containerRef.current.getBoundingClientRect();
    // (e.clientX/clientY works for both mouse and touch pointer events.)
    const tapX = e.clientX - rect.left;
    const tapY = e.clientY - rect.top;

    const b = ball.current;
    // Calculate the vector from the tap to the ball.
    const dx = b.x - tapX;
    const dy = b.y - tapY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist === 0) return;

    // Use force magnitude from settings
    const fx = (dx / dist) * physicsSettings.forceMagnitude;
    const fy = (dy / dist) * physicsSettings.forceMagnitude;

    // Add the force to the ball's current velocity.
    b.vx += fx;
    b.vy += fy;
  };

  // Handler for settings changes
  const handleSettingChange = (setting, value) => {
    setPhysicsSettings(prev => ({
      ...prev,
      [setting]: parseFloat(value)
    }));
  };

  return (
    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      className="basketball-game"
    >
      {/* Physics Settings Panel */}
      {/*<div className="physics-panel">
        <div className="physics-control">
          <label>Gravity: {physicsSettings.gravity}</label>
          <input
            type="range"
            min="0"
            max="2"
            step="0.1"
            value={physicsSettings.gravity}
            onChange={(e) => handleSettingChange('gravity', e.target.value)}
          />
        </div>
        
        <div className="physics-control">
          <label>Force: {physicsSettings.forceMagnitude}</label>
          <input
            type="range"
            min="1"
            max="20"
            step="0.5"
            value={physicsSettings.forceMagnitude}
            onChange={(e) => handleSettingChange('forceMagnitude', e.target.value)}
          />
        </div>
        
        <div className="physics-control">
          <label>Bounce: {physicsSettings.restitution}</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={physicsSettings.restitution}
            onChange={(e) => handleSettingChange('restitution', e.target.value)}
          />
        </div>
      </div>*/}

      {/* Floor */}
      <div 
        className="floor"
        style={{ height: `${FLOOR_HEIGHT}px` }}
      />

      {/* Ball */}
      <div
        ref={ballElRef}
        className="basketball"
        style={{
          width: ball.current.radius * 2,
          height: ball.current.radius * 2
        }}
      />

      {/* Left Hoop */}
      <div
        className="hoop left"
        style={{ top: hoops.left.y }}
      />

      {/* Right Hoop */}
      <div
        className="hoop"
        style={{
          left: containerSize.current.width - 80,
          top: hoops.right.y
        }}
      />

      {/* Score */}
      <div className="score">
        Score: {score}
      </div>
    </div>
  );
};

export default TapBasketballGame;
